import React from "react";
import LegalLayout, { Section, Bullets } from "@/components/LegalLayout";

const CONTACT = "support@karyowads.com";

export default function TermsOfService() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="These Terms of Service govern your access to and use of KARYOW AI Ads Manager at karyowads.com. Please read them carefully."
      lastUpdated="June 1, 2026"
    >
      <Section title="1. Acceptance of Terms">
        <p>
          By accessing or using KARYOW AI Ads Manager (the "Service"), operated by KARYOW ("we", "us", "our"),
          you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.
        </p>
      </Section>

      <Section title="2. Eligibility & Accounts">
        <p>
          You must be at least 18 years old and able to form a binding contract to use the Service. You are
          responsible for maintaining the confidentiality of your account credentials and for all activity under
          your account. Notify us immediately of any unauthorized use.
        </p>
      </Section>

      <Section title="3. Description of Service">
        <p>
          KARYOW AI Ads Manager provides tools to organize advertising campaigns, generate ad content using AI,
          analyze performance, and connect to Meta (Facebook/Instagram) advertising accounts. Features may change,
          be added, or be removed at our discretion.
        </p>
      </Section>

      <Section title="4. Acceptable Use">
        <p>You agree not to:</p>
        <Bullets items={[
          "Use the Service for any unlawful, deceptive, or fraudulent purpose.",
          "Create advertising content that violates Meta's advertising policies or applicable law.",
          "Attempt to access accounts, data, or systems you are not authorized to access.",
          "Reverse engineer, scrape, overload, or disrupt the Service.",
          "Upload malware or infringe intellectual property or privacy rights.",
          "Resell or sublicense the Service without our written consent.",
        ]} />
      </Section>

      <Section title="5. Meta Integration">
        <p>
          When you connect a Meta ad account, you authorize us to access and manage advertising data on your
          behalf. You are responsible for complying with Meta's terms and policies. We are not affiliated with,
          endorsed by, or sponsored by Meta Platforms, Inc.
        </p>
      </Section>

      <Section title="6. AI-Generated Content">
        <p>
          The Service uses artificial intelligence to generate ad briefs, copy, and images. AI output may be
          inaccurate, incomplete, or unsuitable. You are solely responsible for reviewing, editing, and approving
          any content before publishing it. You retain rights to the inputs you provide and, subject to these
          Terms and third-party model terms, to the outputs you generate.
        </p>
      </Section>

      <Section title="7. Subscriptions & Billing">
        <Bullets items={[
          "The Service may be offered on free and paid subscription plans. Paid plans are billed in advance on a recurring basis (monthly or annually) as shown at checkout.",
          "By subscribing, you authorize us and our payment processor to charge your payment method for the applicable fees, including taxes, on each renewal.",
          "Subscriptions renew automatically until cancelled. You may cancel at any time from your account settings; cancellation takes effect at the end of the current billing period.",
          "We may change plan pricing or features with prior notice. Changes apply from your next billing cycle.",
          "Failure to pay may result in suspension or termination of your access.",
        ]} />
      </Section>

      <Section title="8. Refund Policy">
        <Bullets items={[
          "You may request a full refund within 14 days of your initial paid subscription purchase if you are not satisfied ('cooling-off period').",
          "Renewal charges are generally non-refundable; however, if you cancel within 48 hours of an automatic renewal, you may request a refund of that renewal charge.",
          "Refunds are not provided for partial billing periods, unused AI generation credits, or accounts terminated for violation of these Terms.",
          "Approved refunds are issued to the original payment method within 5–10 business days.",
          "To request a refund, contact " + CONTACT + " with your account email and order details.",
        ]} />
      </Section>

      <Section title="9. Affiliate Program Terms">
        <p>
          KARYOW may offer an affiliate/referral program allowing you to earn commissions for referring new paying
          customers. Participation is subject to the following:
        </p>
        <Bullets items={[
          "Affiliates receive a unique referral link and earn a commission on qualifying subscription payments made by referred customers, at the rate published in the affiliate dashboard.",
          "Commissions are earned only on completed, non-refunded payments and become payable after any applicable refund window has passed.",
          "Self-referrals, spam, misleading claims, trademark bidding, cookie stuffing, and any deceptive or unlawful promotion are prohibited and may result in forfeiture of commissions and termination.",
          "Affiliates must clearly disclose their affiliate relationship as required by law.",
          "Payouts are made once your balance reaches the minimum threshold shown in the affiliate dashboard, using the payout method you provide.",
          "We may modify or discontinue the affiliate program, adjust commission rates, and reverse commissions relating to fraudulent or refunded transactions at any time.",
        ]} />
      </Section>

      <Section title="10. Intellectual Property">
        <p>
          The Service, including its software, design, branding, and content (excluding your data and generated
          content), is owned by KARYOW and protected by intellectual property laws. We grant you a limited,
          non-exclusive, non-transferable license to use the Service in accordance with these Terms.
        </p>
      </Section>

      <Section title="11. Termination">
        <p>
          You may stop using the Service at any time. We may suspend or terminate your access if you breach these
          Terms or use the Service in a manner that could cause harm or legal liability. Upon termination, your
          right to use the Service ceases, and you may request deletion of your data via karyowads.com/data-deletion.
        </p>
      </Section>

      <Section title="12. Disclaimers">
        <p>
          The Service is provided "as is" and "as available" without warranties of any kind, express or implied.
          We do not warrant that the Service will be uninterrupted, error-free, or that AI outputs or performance
          predictions will produce any particular result.
        </p>
      </Section>

      <Section title="13. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, KARYOW will not be liable for any indirect, incidental,
          special, consequential, or punitive damages, or for lost profits or ad spend, arising from your use of
          the Service. Our total liability for any claim shall not exceed the amount you paid us in the 12 months
          preceding the claim.
        </p>
      </Section>

      <Section title="14. Indemnification">
        <p>
          You agree to indemnify and hold harmless KARYOW from any claims, damages, and expenses arising out of
          your use of the Service, your content, or your violation of these Terms or applicable law.
        </p>
      </Section>

      <Section title="15. Governing Law & Changes">
        <p>
          These Terms are governed by applicable law in the jurisdiction where KARYOW operates. We may update
          these Terms from time to time; material changes will be posted here with a revised date. Continued use
          after changes constitutes acceptance.
        </p>
      </Section>

      <Section title="16. Contact">
        <p>
          Questions about these Terms? Contact us at <span className="text-lime-400">{CONTACT}</span>.
        </p>
      </Section>
    </LegalLayout>
  );
}
